import React from "react";
import Slider from "react-slick";
import PropTypes from "prop-types";
import Image from "next/image";
import Media from "../../Generic/Carousel/CarouselMedia";

const PDPCarouselSettings = {
  dots: false,
  arrows: false,
  slidesToShow: 5,
  slidesToScroll: 1,
  responsive: [
    { breakpoint: 1023, settings: { slidesToShow: 6 } },
    { breakpoint: 767, settings: { slidesToShow: 5 } },
  ],
};
const getThumbnailSlides = (width) => {
    if (width >= 834 && width <= 1112) {
        return 6;
    }

    if (width < 768) {
        return 5;
    }

    return 5;
};
const PDPCarousal = ({ imageList }) => {
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [thumbnailSlides, setThumbnailSlides] = React.useState(5);
  const [liveMessage, setLiveMessage] = React.useState("");
  const sliderOneRef = React.useRef();
  const sliderTwoRef = React.useRef();


    React.useEffect(() => {
        const setSlidesForViewport = () => {
            setThumbnailSlides(getThumbnailSlides(window.innerWidth));
        };

        setSlidesForViewport();
        window.addEventListener("resize", setSlidesForViewport);

        return () => {
            window.removeEventListener("resize", setSlidesForViewport);
        };
    }, []);
  // Prevent Enter/Space from also causing a synthetic click (double activation)
  const ignoreNextClickRef = React.useRef(false);

  const afterChangeImage = (index) => {
    setCurrentSlide(index);
    // Announce the new selected image for screen readers
    const item = imageList[index];
    const altText = item?.miniatureImage?.altText || `Imagem do produto ${index + 1}`;
    setLiveMessage(`${altText}, selecionada`);
  };

  const handelNavArrowClick = (type) => {
    if (!sliderOneRef.current) return;

    if (type === "next") {
      sliderOneRef.current.slickGoTo(currentSlide + 1);
    } else if (type === "prev") {
      sliderOneRef.current.slickGoTo(currentSlide - 1);
    }
  };

  const activateThumbnail = (index) => {
    // Update state first so aria-selected updates correctly
    setCurrentSlide(index);

    if (sliderOneRef.current) {
      sliderOneRef.current.slickGoTo(index);
    }
    // IMPORTANT: no forced focus() here -> avoids second announcement
  };

  const handleThumbnailKeyDown = (index, event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.stopPropagation();

      ignoreNextClickRef.current = true;
      activateThumbnail(index);

      setTimeout(() => {
        ignoreNextClickRef.current = false;
      }, 0);
    }
  };

  // Use a MutationObserver to continuously override react-slick's aria-hidden="true"
  // assignments on thumbnail slides. react-slick sets aria-hidden="true" on off-screen
  // slides dynamically, which traps focusable buttons inside hidden elements (WCAG 4.1.2).
  React.useEffect(() => {
    const list = sliderTwoRef.current?.innerSlider?.list;
    if (!list) return;

    const forceAriaVisible = () => {
      list.querySelectorAll('.slick-slide').forEach((slide) => {
        slide.setAttribute('aria-hidden', 'false');
      });
    };

    forceAriaVisible();

    const observer = new MutationObserver((mutations) => {
      const needsUpdate = mutations.some(
        (m) =>
          m.type === 'attributes' &&
          m.attributeName === 'aria-hidden' &&
          m.target.getAttribute('aria-hidden') === 'true'
      );
      if (needsUpdate) forceAriaVisible();
    });

    observer.observe(list, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      subtree: true,
    });

    return () => observer.disconnect();
  }, [imageList, thumbnailSlides]);

  return (
    <div className="pdpGallery w-full lg:w-6/12 min-h-[416px] mt-25 md:mt-0 float-left px-25 md:px-0 lg:min-h-658 lg:min-w-720  bg-no-repeat z-1 block pdpCarousel-animate">
      <div className="pt-10 md:pt-50 w-full lg:w-[68%] mx-auto">
        <div className="flex justify-around justify-items-center">
          <div className="w-full">
            {/* Main Slider */}
            <Slider
              asNavFor={sliderTwoRef.current}
              infinite={true}
              ref={sliderOneRef}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              dots={false}
              arrows={false}
              className="w-full"
              afterChange={(current) => afterChangeImage(current)}
              responsive={[
                {
                  breakpoint: 768,
                  settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    dots: false,
                    arrows: false,
                  },
                },
              ]}
            >
              {imageList &&
                imageList.map((list, index) => (
                  <Media
                    key={index}
                    component="bannerPicture"
                    srcset={list?.carouselImage?.desktopImage?.url}
                    image={list?.carouselImage?.smartphoneImage?.url}
                    altText={list?.carouselImage?.altText}
                    priority={true}
                    className="w-full rounded-10 mx-auto object-cover object-center"
                  />
                ))}
            </Slider>
          </div>
        </div>

        {/* Pack shot Image Slider */}
        <div className="prodGalary justify-center w-full md:w-full lg:w-full sm:w-full mx-auto flex items-center mt-17 lg:mt-30 relative pt-30 px-0 lg:px-0">
          <div className="w-10p mr-17 hidden md:block">
            <button
              type="button"
              tabIndex="0"
              style={{ width: "50px" }}
              onClick={() => handelNavArrowClick("prev")}
              data-action-detail="next-arrow"
              className="arrow-prev"
              aria-label="Ver imagem anterior do produto."
            >
              <Image
                width={20}
                height={38}
                src="https://images.ctfassets.net/bpsnf1n7mj27/4iwUIm55tgWznSJGwSe79R/3ac7cf3dfa5cd4ae5198c7a997598a56/LeftArrow.svg"
                alt=""
                aria-hidden="true"
              />
            </button>
          </div>

          <div className="w-full md:w-90p">
            <Slider
             key={`thumb-${thumbnailSlides}`}
              {...PDPCarouselSettings}
              slidesToShow={thumbnailSlides}
              infinite={false}
              swipeToSlide
              focusOnSelect={false}
              accessibility={false}
              asNavFor={sliderOneRef.current}
              ref={sliderTwoRef}
              className="h-full w-full p-0"
            >
              {imageList &&
                imageList.map((itemurl, index) => {
                  const imageNumber = index + 1;
                  const isSelected = currentSlide === index;

                  const altText =
                    itemurl?.carouselImage?.altText ||
                    `Imagem do produto ${imageNumber}`;

                  return (
                    <div className="p-0 lg:p-6" key={index}>
                      <button
                        type="button"
                        className={"border-2 border-gray-300 shadow-md cursor-pointer rounded-10 h-55 sm:max-w-[62px] sm:h-[62px] lg:max-w-55 lg:h-55 xl:max-w-75 xl:h-75 object-cover pdpThumnailImages"}
                        role="button"
                        aria-label={
                          isSelected
                            ? `${altText} ${imageNumber} of ${imageList.length} selected`
                            : `${altText} ${imageNumber} of ${imageList.length} not selected`
                        }
                        onKeyDown={(e) => handleThumbnailKeyDown(index, e)}
                        onClick={(e) => {
                          if (ignoreNextClickRef.current) return;
                          activateThumbnail(index);
                        }}
                      >
                        <Image
                          width={70}
                          height={70}
                          alt={altText}
                          aria-hidden="true"
                          src={itemurl.miniatureImage?.image?.url}
                          className="w-full h-full object-cover rounded-10"
                          sizes="(max-width: 768px) 50vw (max-width: 1200px) 100vw 70vw"
                        />
                      </button>
                    </div>
                  );
                })}
            </Slider>
          </div>

          <div className="w-10p ml-10 hidden md:block">
            <button
              type="button"
              tabIndex="0"
              style={{ width: "50px" }}
              onClick={() => handelNavArrowClick("next")}
              data-action-detail="prev-arrow"
              className="arrow-next"
              aria-label="Ver próxima imagem do produto."
            >
              <Image
                width={20}
                height={38}
                src="https://images.ctfassets.net/bpsnf1n7mj27/2H67jgOXZDOUVkOT1ypREx/a73daa0551276f9440f24022a92378b3/RightArrow.svg"
                alt=""
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

PDPCarousal.propTypes = {
  imageList: PropTypes.array.isRequired,
};

export default PDPCarousal;